/**
 * Adding facts by pointing at a page, instead of typing them.
 *
 * The order on screen is the whole design. 「リンクから追加」 comes first because it
 * is what somebody with a website should do, and 「手動で追加」 sits under it
 * unchanged - the same form it has always been. Nothing here replaces typing; it
 * removes the case where typing was the only option.
 *
 * WHAT IT PRODUCES IS DRAFTS. This queues a read; the worker writes rows at
 * status DRAFT with the sentence each value came from, and they appear in the
 * list behind this panel to be checked, edited and approved exactly like a typed
 * row. Generation cannot see any of them until somebody presses 承認 - which was
 * already true of everything, and is why there is no separate review screen.
 *
 * SEVERAL LINKS AT ONCE, because a clinic's facts are spread over several pages
 * and making somebody paste-and-wait five times is the thing this feature exists
 * to avoid. Each line becomes its own source and its own job, so one bad URL
 * fails on its own rather than losing the other four.
 */
import { useEffect, useState } from 'react';
import { ApiError, get, post } from '../api.ts';
import { Pill } from './ui.tsx';
import { Modal, ModalForm } from './Modal.tsx';
import { PageText } from './PageText.tsx';
import { Textarea } from './Textarea.tsx';

interface Registered {
  url: string;
  ok: boolean;
  message?: string;
  /**
   * The refusal's code, when there was one - so a host the clinic is not
   * allowed to read can be told apart from a page that 404'd. Only
   * `host_not_allowed` is acted on; everything else is a message to display.
   */
  code?: string;
  /** The host that was refused, for the override dialog to name. */
  host?: string;
}

/** The hostname of a URL, or null if it is not one. Used only for display. */
function hostOf(url: string): string | null {
  try {
    return new URL(url).hostname;
  } catch {
    return null;
  }
}

/** One registered page, with how its last reading went. */
interface Source {
  id: string;
  url: string;
  resource: string | null;
  status: string | null;
  proposed: number | null;
  rejected: number | null;
  duplicates: number | null;
  returned: number | null;
  unchanged: boolean | null;
  error: string | null;
  /** Whether the last reading kept the page's text. See migration 081. */
  has_text?: boolean | null;
}

/**
 * What a finished reading actually did, in a sentence.
 *
 * The reason this exists: a clinic reported that a staff page 「did not fetch the
 * doctors」, and the only thing anybody could see was that no new rows had
 * appeared. Four different outcomes look identical from the list - the page says
 * nothing, the page has not changed, everything on it was already registered,
 * and the model read it and offered nothing - and only the last is a fault. The
 * first three are the system working and should not send anybody looking for a
 * bug; the last one should.
 */
function outcomeOf(source: Source): { tone: 'ok' | 'muted' | 'danger'; text: string } {
  if (source.status === 'FAILED') {
    return { tone: 'danger', text: `失敗: ${source.error ?? '原因不明'}` };
  }
  if (source.status === null) return { tone: 'muted', text: '未取得' };
  if (source.status !== 'DONE') return { tone: 'muted', text: '読み取り中…' };
  if (source.unchanged) return { tone: 'muted', text: '前回から変更なし（費用なし）' };

  const added = source.proposed ?? 0;
  const dup = source.duplicates ?? 0;
  const rejected = source.rejected ?? 0;
  if (added > 0) {
    return {
      tone: 'ok',
      text: `${added}件を下書きに追加${dup > 0 ? `（${dup}件は登録済みのため省略）` : ''}`,
    };
  }
  if (dup > 0) return { tone: 'ok', text: `${dup}件すべて登録済みでした` };
  if (rejected > 0) {
    // Read something, but none of it could be traced back to the page.
    return { tone: 'danger', text: `${rejected}件を読み取りましたが、引用が一致せず破棄しました` };
  }
  /*
   * The one that used to be indistinguishable. `returned` is null on jobs
   * written before migration 067, so it is only asserted when known.
   */
  if (source.returned === 0) {
    return { tone: 'danger', text: 'このページからは何も読み取れませんでした（もう一度お試しください）' };
  }
  return { tone: 'muted', text: '追加された項目はありません' };
}

export function AddFromLink({
  /** Restricts what the extraction looks for. Omit to read a whole page. */
  resource,
  /** Which store the facts belong to. '' is clinic-wide. */
  storeId,
  /**
   * Set on the JMC screens, absent on the clinic's own.
   *
   * JMC had no way to do this at all: an operator setting a clinic up saw
   * 「ペルソナを追加」 on the fact panel and no way to read the clinic's website,
   * which is where those facts are supposed to come from - so they had to ask
   * the clinic to do it. The routes differ only in how the clinic is named
   * (path versus session), so the panel takes an id rather than being written
   * twice.
   */
  clinicId,
  /** Told when at least one page was queued, so the list can re-read. */
  onQueued,
}: {
  // Explicitly `| undefined`: the project runs exactExactOptionalPropertyTypes,
  // so an omitted prop and one passed as undefined are different types.
  resource?: string | undefined;
  storeId?: string | undefined;
  clinicId?: string | undefined;
  onQueued: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<Registered[]>([]);
  const [sources, setSources] = useState<Source[]>([]);

  /*
   * Re-read while the panel is open, because a reading takes tens of seconds and
   * finishes in a worker. Polled rather than pushed: this is one small query
   * against rows the operator is already looking at, and it stops the moment
   * the panel closes or every job has finished.
   */
  useEffect(() => {
    if (!open) return undefined;
    let live = true;
    const read = async () => {
      try {
        const answer = await get<{ items: Source[] }>(
          clinicId ? `/admin/clinics/${clinicId}/sources` : '/sources',
        );
        if (!live) return;
        const mine = resource
          ? answer.items.filter((item) => item.resource === resource || item.resource === null)
          : answer.items;
        setSources(mine);
      } catch {
        // A failed poll is not worth reporting: the next one is a second away
        // and the panel still works without it.
      }
    };
    void read();
    const timer = setInterval(() => void read(), 4000);
    return () => {
      live = false;
      clearInterval(timer);
    };
  }, [open, resource, results, clinicId]);

  const [rereading, setRereading] = useState<string | null>(null);
  const [overriding, setOverriding] = useState(false);
  const [persistHosts, setPersistHosts] = useState(false);

  /**
   * Ask for a page to be read again.
   *
   * The route forces it past the unchanged-digest check, so this is 「読み直す」
   * and not 「変わっていたら読む」. The list is polling, so the queued state
   * appears on its own a moment later.
   */
  async function reread(sourceId: string) {
    setRereading(sourceId);
    try {
      await post(
        clinicId
          ? `/admin/clinics/${clinicId}/sources/${sourceId}/harvest`
          : `/sources/${sourceId}/harvest`,
        // The button IS the request to read it again; an unchanged page is not
        // an answer to it.
        { force: true },
      );
      onQueued();
    } catch {
      // The row's own status is where a failure shows; a toast here would say
      // the same thing twice.
    } finally {
      setRereading(null);
    }
  }

  /*
   * One line, one page. Split on newlines rather than offering an "add another"
   * button: pasting a list is what somebody actually has, and a growing list of
   * inputs is more machinery for the same result.
   */
  const urls = text
    .split(/[\n\s]+/)
    .map((line) => line.trim())
    .filter((line) => line !== '');

  /**
   * Register each URL, one request per line.
   *
   * @param allowAnyHost JMC only - read these even though their host is not
   *   allowed for this clinic. Never set on the first pass: the override is
   *   offered only AFTER a refusal, so an operator cannot arm it before knowing
   *   it was needed, and a batch that did not need it never carries it into the
   *   audit log.
   */
  async function register(targets: string[], allowAnyHost = false): Promise<Registered[]> {
    const done: Registered[] = [];
    for (const url of targets) {
      try {
        await post(clinicId ? `/admin/clinics/${clinicId}/sources` : '/sources', {
          url,
          ...(resource ? { resource } : {}),
          ...(storeId ? { storeId } : {}),
          ...(allowAnyHost ? { allowAnyHost: true } : {}),
        });
        done.push({ url, ok: true });
      } catch (err) {
        /*
         * Reported per URL and the loop continues. One mistyped address out of
         * five must not discard the other four - which is what a single
         * all-or-nothing request would do.
         */
        done.push({
          url,
          ok: false,
          message:
            err instanceof ApiError
              ? [err.message, ...err.fields].join(' / ')
              : '登録できませんでした',
          ...(err instanceof ApiError ? { code: err.code } : {}),
          ...(hostOf(url) ? { host: hostOf(url)! } : {}),
        });
      }
    }
    return done;
  }

  async function submit() {
    setBusy(true);
    setResults([]);
    const done = await register(urls);
    setResults(done);
    setBusy(false);
    if (done.some((entry) => entry.ok)) {
      setText('');
      onQueued();
    }
  }

  /*
   * The refused-for-host lines, and the distinct hosts behind them.
   *
   * Read off the RESULTS rather than tracked separately, so the retry can only
   * ever cover URLs that were actually refused - the allowed half of a mixed
   * batch is already registered and must not be sent twice.
   */
  const refused = results.filter((entry) => entry.code === 'host_not_allowed');
  const refusedHosts = [...new Set(refused.map((entry) => entry.host).filter(Boolean))] as string[];

  /**
   * Read the refused pages with JMC's authority, and optionally trust the
   * domains for good.
   *
   * The grants go FIRST and separately: if a later read times out, the durable
   * permission has still landed and the operator is not left having agreed to
   * something that did not happen. `allowAnyHost` still rides on the retry even
   * when the domain was just granted - the grant may not be visible to a
   * request already in flight, and the flag is idempotent.
   */
  async function override(persist: boolean) {
    if (persist) {
      for (const host of refusedHosts) {
        await post(`/admin/clinics/${clinicId!}/allowed-hosts`, {
          host,
          includeSubdomains: false,
        });
      }
    }
    const retried = await register(
      refused.map((entry) => entry.url),
      true,
    );
    // One honest list: the first pass's successes, plus what the retry did.
    setResults([...results.filter((entry) => entry.code !== 'host_not_allowed'), ...retried]);
    if (retried.some((entry) => entry.ok)) {
      setText('');
      onQueued();
    }
  }

  return (
    <>
      <button className="btn btn--sm" type="button" onClick={() => setOpen(true)}>
        ＋ リンクから追加
      </button>

      {/*
        A dialog, not a panel that unfolds above the list.

        This one pushed the whole table down the moment it opened, so pressing
        「リンクから追加」 moved the rows somebody was reading - and then it stayed
        open, polling, while they scrolled past it to look at the drafts it had
        produced. A reading takes tens of seconds and its results appear in the
        list behind; the right shape for that is something you open, start, and
        close, with the list still where you left it.
      */}
      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
          setResults([]);
        }}
        title="リンクから追加"
        caption="ページに書かれている内容を読み取り、下書きとして登録します。"
        wide
      >
        <p className="muted small" style={{ marginTop: 0 }}>
          {clinicId ? '読み取りたいページのURLを貼ってください。' : 'クリニックのウェブページのURLを貼ってください。'}
          {resource ? 'この項目の候補' : '候補'}を読み取り、<strong>下書き</strong>
          として登録します。内容を確認して「承認」するまで、記事生成には使われません。
        </p>

        <div className="field">
          <label htmlFor="harvest-urls">URL（1行に1つ。複数まとめて貼れます）</label>
          <Textarea
            id="harvest-urls"
            minRows={4}
            value={text}
            placeholder={'https://your-clinic.jp/menu\nhttps://your-clinic.jp/doctor'}
            onChange={(event) => setText(event.target.value)}
          />
          <span className="field__hint">
            {clinicId
              ? '自院サイトのURLと、JMCが許可したドメインのURLを取得できます。許可されていないドメインは、読み取り後に表示される確認画面からJMCの権限で取得できます。'
              : '自院サイトのURLと、JMCが許可したドメインのURLを取得できます。ほかのドメインを追加したい場合はJMCの担当者にご連絡ください。'}
            トップページを指定した場合はそのページだけを読み、
            下層ページは候補として一覧に追加します。個別ページを指定した場合は、そのページ以下も読みます。
          </span>
        </div>

        <div className="editpanel__actions">
          <button
            className="btn btn--sm"
            type="button"
            disabled={busy || urls.length === 0}
            onClick={() => void submit()}
          >
            {busy ? '読み取り中…' : `${urls.length || ''}件のURLを読み取る`}
          </button>
          <button
            className="btn btn--ghost btn--sm"
            type="button"
            disabled={busy}
            onClick={() => {
              setOpen(false);
              setResults([]);
            }}
          >
            閉じる
          </button>
          <span className="muted small">
            読み取りは数十秒かかります。閉じても続き、結果は一覧に下書きとして現れます。
          </span>
        </div>

        {results.length > 0 && (
          <ul className="findings" style={{ marginTop: 10 }}>
            {results.map((entry) => (
              <li
                key={entry.url}
                className={`findings__item${entry.ok ? '' : ' findings__item--error'}`}
              >
                <Pill tone={entry.ok ? 'ok' : 'danger'}>{entry.ok ? '受付' : '失敗'}</Pill>
                <span className="muted small">
                  {entry.url}
                  {entry.message ? ` — ${entry.message}` : ''}
                </span>
              </li>
            ))}
          </ul>
        )}

        {/*
          The override, offered only after a refusal and only to JMC.

          Rejected alternatives: a checkbox armed before submitting (the
          operator arms an override before knowing one is needed, so every batch
          carries it and the audit trail stops meaning anything), and per-URL
          controls (the textarea is a paste target, there is nothing to hang
          them on).
        */}
        {clinicId && refused.length > 0 && (
          <p className="muted small" style={{ marginTop: 10 }}>
            許可されていないドメインのため {refused.length}件 を読み取れませんでした。
            <button
              className="btn btn--sm"
              type="button"
              style={{ marginInlineStart: 8 }}
              onClick={() => setOverriding(true)}
            >
              JMCの権限で読み取る
            </button>
          </p>
        )}

        <ModalForm
          open={overriding}
          onClose={() => setOverriding(false)}
          title="許可されていないドメインを読み取ります"
          submitLabel="このまま読み取る"
          danger
          onSubmit={async () => {
            await override(persistHosts);
            setPersistHosts(false);
          }}
        >
          {/*
            One column, top to bottom: what is being overridden, what happens,
            what does not, and the choice last.

            Wrapped in a single full-width cell because the ModalForm body is the
            two-column FORM grid: loose children landed one per cell, which put
            the host list, the consequences and the checkbox side by side in
            whatever order the grid chose. And the consequences are a plain
            bulleted list, not `.checklist` - pink ticks read as "all fine",
            which is the opposite of a warning, and its flex rows split a line
            containing <strong> into columns of one-character-wide text.
          */}
          <div className="field--full overridewarn">
            <div className="alert alert--warn">
              次のドメインは、このクリニックに許可されていません。
              <div className="overridewarn__hosts">
                {refusedHosts.map((host) => (
                  <code key={host}>{host}</code>
                ))}
              </div>
            </div>

            <p className="form__section">読み取ると</p>
            <ul className="overridewarn__list">
              <li>
                読み取った内容は<strong>このクリニックの情報</strong>
                として下書き登録され、承認後はこのクリニックの記事に使われます。
              </li>
              {/*
                The honesty point. An override REGISTERS the page, and re-reading
                a registered page never re-checks its host - so 常時許可しない means
                the domain is not trusted, not that the read was temporary.
              */}
              <li>登録したページは、ドメインを常時許可しなくても再読み取りできる状態で残ります。</li>
              <li>この操作は、実行した担当者名とともに監査ログに記録されます。</li>
            </ul>

            <p className="form__section">変わらないこと</p>
            <ul className="overridewarn__list">
              <li>robots.txt は引き続き守られます。</li>
              <li>社内・プライベートなアドレスは引き続き取得できません。</li>
            </ul>

            {/* Last, so it is read after the consequences - and off by default. */}
            <div className="notice overridewarn__persist">
              <label className="checkline">
                <input
                  type="checkbox"
                  checked={persistHosts}
                  onChange={(event) => setPersistHosts(event.target.checked)}
                />
                <span>このドメインをこのクリニックに常時許可する</span>
              </label>
              <span className="field__hint">
                オンにすると、次回からクリニック側でもこのドメインのURLを登録できます。サブドメインは含まれません。あとから「AIが読み取れるドメイン」で変更できます。
              </span>
            </div>
          </div>
        </ModalForm>

        {sources.length > 0 && (
          <>
            <p className="form__section">登録済みのURLと、前回の読み取り結果</p>
            <ul className="findings" style={{ marginTop: 0 }}>
              {sources.map((source) => {
                const outcome = outcomeOf(source);
                return (
                  <li key={source.id} className="findings__item">
                    <Pill tone={outcome.tone}>{outcome.text}</Pill>
                    <span className="muted small">{source.url}</span>
                    {/*
                      What the reading actually saw, and a way to keep the part
                      of it no column fits. Only where there is text: an older
                      job stored none, and a control that opens an empty panel
                      is a control that teaches somebody it is broken.
                    */}
                    <span className="findings__actions">
                      {/*
                        Read it again, and mean it.
                        
                        An unchanged page normally answers 「変更なし」 and costs
                        nothing, which is right for a scheduled re-read and wrong
                        for somebody pressing this: the page may have been read
                        from a screen that was only looking for one kind of fact,
                        or the extraction may simply have improved. So this asks
                        for the read regardless.
                      */}
                      <button
                        className="btn btn--ghost btn--sm"
                        type="button"
                        disabled={rereading === source.id || source.status === 'QUEUED'}
                        onClick={() => void reread(source.id)}
                      >
                        {rereading === source.id ? '受付中…' : '再読み取り'}
                      </button>
                      {source.has_text && (
                        <PageText
                          sourceId={source.id}
                          storeId={storeId}
                          {...(clinicId ? { clinicId } : {})}
                          onSaved={onQueued}
                        />
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </Modal>
    </>
  );
}
